import React from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@mui/styles';

import Drawer from '@mui/material/Drawer';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';

import LogoUrl from '../../../../resources/logo.svg';
import TwitterIconUrl from '../../../../resources/icons/icon-twitter.svg';
import LinkedinIconUrl from '../../../../resources/icons/icon-linkedin.svg';
import YoutubeIconUrl from '../../../../resources/icons/icon-youtube.svg';
import DownloadIconUrl from '../../../../resources/icons/icon-download.svg';

import { 
  WEBSITE_URL,
  TWITTER_URL,
  YOUTUBE_URL,
  LINKEDIN_URL,
  MENU_HIERARCHY, 
  MENU_DISCOVER_NUMBERS_URL, 
  MENU_DESKTOP_WIDTH,
} from '../../../../config';

import ShareButton from './ShareButton';
import { getIcon } from '../index';

import styles from '../styles';

class DesktopAppMenu extends React.PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      currentHighlightedItem: null,
      isHover: false,
    }
  }
  /**
   * HANDLE
   */
  handleMouseEnter(evt, params = null) {
    const setState = {
      isHover: true,
    };
    if (params) {
      setState.currentHighlightedItem = params;
    }
    this.setState(setState);
  }
  
  handleMouseLeave(evt) {
    const { relatedTarget, toElement } = evt.nativeEvent;
    if (relatedTarget) {
      if (relatedTarget.classList.contains('bookmarkButton')) {
        return;
      }
      if (relatedTarget.closest('.subDrawer')) {
        return;
      }
    }
    if (toElement) {
      if (toElement.classList.contains('bookmarkButton')) {
        return;
      }
      if (toElement.closest('.subDrawer')) {
        return;
      }
    }
    this.setState({ isHover: false });
  }

  /**
   * RENDER
   */
  renderMainDrawer() {
    const { classes, page, fullscreen, fileURL, onNavigate } = this.props;
    return (
      <Drawer
        className={classes.mainDrawer}
        variant="persistent"
        anchor="left"
        open={!fullscreen}
      >
        <Box className={classes.mainDrawerHeader}>
          <a href={WEBSITE_URL} target="_blank" rel="noopener noreferrer">
            <img src={LogoUrl} alt="Agence de la biomédecine" />
          </a>
        </Box>
        <List>
          {MENU_HIERARCHY.map(item => {
            const isFocus = page >= item.pages[0] && page <= item.pages[1];
            return (
              <ListItem key={item.label} className={`${classes.listItem} ${item.color} ${isFocus ? 'focus' : ''}`} disablePadding>
                <ListItemButton
                  className="bookmarkButton"
                  onClick={() => onNavigate(item.pages[0])}
                  onMouseEnter={evt => this.handleMouseEnter(evt, item)}
                  onMouseLeave={(evt, b) => this.handleMouseLeave(evt, item, b)}
                >
                  <ListItemIcon>
                    <img className={classes.menuIcon} src={getIcon(item.icon)} alt={item.label} />
                  </ListItemIcon>
                  <ListItemText
                    className={`${classes.drawerLabel} ${item.color} category`}
                    primary={<Typography variant="h3">{item.label}</Typography>}
                    secondary={<span className={`listItemFocus ${item.color}`}></span>}
                  />
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>

        <Box sx={{ flex: 1 }}></Box>

        <Stack sx={{ paddingLeft: 4, paddingRight: 4 }} alignItems="center" spacing={1}>
          <Button
            className={classes.listItemEndButton}
            variant="contained"
            color="inherit"
            onClick={() => window.open(MENU_DISCOVER_NUMBERS_URL, '_blank')}
          >
            <Typography variant="h3">DATA</Typography>
          </Button>
          <Typography sx={theme => ({ color: theme.palette.blue.main, textAlign: 'center' })} variant="body1">
            Consultez nos données 2021 par thématique
          </Typography>
        </Stack>

        <ShareButton classes={classes} />

        <ListItem key="download" className={classes.listItem} disablePadding>
          <ListItemButton
            onClick={() => {
              const link = document.createElement("a");
              link.href = fileURL;
              link.download = 'Agence de la biomédecine - Rapport annuel 2021.pdf';
              link.click();
            }}
          >
            <ListItemIcon>
              <img className={classes.menuIcon} src={DownloadIconUrl} alt="Télécharger le rapport" />
            </ListItemIcon>
            <ListItemText className={classes.drawerLabel} primary={<Typography variant="h4">Télécharger le rapport</Typography>} />
          </ListItemButton>
        </ListItem>

        <ListItem key="social" className={classes.listItem} disableGutters disablePadding>
          <Stack className={classes.socialList} direction="row" justifyContent="flex-end" spacing={0.25}>
            <IconButton key="twitter" size="small" onClick={() => window.open(TWITTER_URL, '_blank')}>
              <img src={TwitterIconUrl} alt="Twitter" />
            </IconButton>
            <IconButton key="facebook" size="small" onClick={() => window.open(LINKEDIN_URL, '_blank')}>
              <img src={LinkedinIconUrl} alt="Linkedin" />
            </IconButton>
            <IconButton key="linkedin" size="small" onClick={() => window.open(YOUTUBE_URL, '_blank')}>
              <img src={YoutubeIconUrl} alt="Youtube" />
            </IconButton>
          </Stack>
        </ListItem>
      </Drawer>
    );
  }

  renderSubDrawer() {
    const { classes, page, onNavigate } = this.props;
    const { isHover, currentHighlightedItem } = this.state;

    const color = (currentHighlightedItem && currentHighlightedItem.color) || 'darkblue';

    return (
      <Drawer
        className={`${classes.subDrawerContainer} ${classes.subDrawer} ${color} subDrawer`}
        variant="persistent"
        anchor="left"
        open={isHover}
      >
          {currentHighlightedItem && (
            <Box
              className={classes.subDrawerPaper}
              onMouseLeave={evt => this.handleMouseLeave(evt)}
            >
              <Typography variant="h2">{currentHighlightedItem.focusLabel}</Typography>
              <Box sx={{ flex: 1 }}></Box>
              {currentHighlightedItem.list && (
                <List className={classes.subList}>
                  {currentHighlightedItem.list.map(bookmark => {
                    const isFocus = page >= bookmark.pages[0] && page <= bookmark.pages[1];
                    return (
                      <ListItem key={bookmark.label} className={`${classes.subListItem} ${currentHighlightedItem.color} ${isFocus ? 'focus' : ''}`} disablePadding>
                        <ListItemButton onClick={() => onNavigate(bookmark.pages[0])}>
                          <ListItemText
                            className={`${classes.subListItemLabel} ${currentHighlightedItem.color}`}
                            primary={<Typography variant="h4">{bookmark.label}</Typography>}
                            secondary={<span className={`listItemFocus ${currentHighlightedItem.color}`}></span>}
                          />
                        </ListItemButton>
                      </ListItem>
                    );
                  })}
                </List>
              )}
            </Box>
          )}
      </Drawer>
    );
  }

  render() {
    const { classes, fullscreen } = this.props;
    return (
      <Stack
        className={classes.desktopMenuContainer}
        sx={{ width: fullscreen ? 0 : MENU_DESKTOP_WIDTH }}
        direction="row"
      >
        {this.renderMainDrawer()}
        {this.renderSubDrawer()}
      </Stack>
    );
  }
}

DesktopAppMenu.defaultProps = {
  page: 0,
};

DesktopAppMenu.propTypes = {
  // props
  fullscreen: PropTypes.bool,
  page: PropTypes.number,
  fileURL: PropTypes.string,
  // funcs
  onNavigate: PropTypes.func.isRequired,
};

export default withStyles(styles)(DesktopAppMenu);
